import type { Employee } from '../types';

export type AdminLandingPath = '/admin/schedule-hub' | '/admin/superadmin';
export type InvitationLandingPath = '/admin' | '/admin/site#business' | '/admin/onboarding';

export const resolveAdminLandingPath = (
  claims: Record<string, unknown>,
  tenantId?: string
): AdminLandingPath | null => {
  if (claims.superAdmin === true) {
    return tenantId ? '/admin/schedule-hub' : '/admin/superadmin';
  }

  return tenantId && claims.websiteId === tenantId ? '/admin/schedule-hub' : null;
};

export const hasSelectedTenantClaimConflict = (
  claims: Record<string, unknown>,
  selectedTenantId?: string
): boolean => {
  if (claims.superAdmin === true || !selectedTenantId) return false;
  const claimedTenantId = typeof claims.websiteId === 'string' ? claims.websiteId : undefined;
  return Boolean(claimedTenantId && claimedTenantId !== selectedTenantId);
};

export const addTenantQueryToPath = (path: string, tenantId: string): string => {
  const target = new URL(path, 'https://control-plane.invalid');
  target.searchParams.set('tenant', tenantId);
  return `${target.pathname}${target.search}${target.hash}`;
};

export const addTenantQueryToAdminPath = (path: AdminLandingPath, tenantId: string): string => {
  return addTenantQueryToPath(path, tenantId);
};

export const resolveInvitationLandingPath = (
  invitationType: unknown,
  isAdmin: boolean,
  publicBusinessName?: string
): InvitationLandingPath => {
  if (invitationType === 'tenant-owner') {
    return '/admin/onboarding';
  }
  if (isAdmin && !publicBusinessName?.trim()) {
    return '/admin/site#business';
  }

  return '/admin';
};

export const selectSuperAdminDashboardEmployee = (employees: Employee[]): Employee | null => {
  const activeEmployees = employees.filter((employee) => employee.active !== false);
  const candidates = activeEmployees.length > 0 ? activeEmployees : employees;

  return [...candidates].sort((left, right) => {
    if (left.admin !== right.admin) {
      return left.admin ? -1 : 1;
    }

    const leftHasName = Boolean(`${left.firstName} ${left.lastName}`.trim());
    const rightHasName = Boolean(`${right.firstName} ${right.lastName}`.trim());
    if (leftHasName !== rightHasName) {
      return leftHasName ? -1 : 1;
    }

    const leftName = `${left.firstName} ${left.lastName}`.trim();
    const rightName = `${right.firstName} ${right.lastName}`.trim();
    return leftName.localeCompare(rightName) || left.id.localeCompare(right.id);
  })[0] ?? null;
};
